import { CheckCircle2, Info, TriangleAlert, X } from 'lucide-react';
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { cx } from '@/lib/cx';
import { ToastContext, type ToastInput, type ToastTone } from './toast-context';

interface ToastItem extends ToastInput {
  id: number;
}

const icons: Record<ToastTone, ReactNode> = {
  success: <CheckCircle2 className="size-4 text-up" aria-hidden />,
  error: <TriangleAlert className="size-4 text-down" aria-hidden />,
  info: <Info className="size-4 text-muted-ink" aria-hidden />,
};

/** Toasts for action results. Polite live region for success/info, assertive for errors. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const next = useRef(1);

  const dismiss = useCallback((id: number) => setItems((list) => list.filter((t) => t.id !== id)), []);
  const toast = useCallback(
    (t: ToastInput) => {
      const id = next.current++;
      setItems((list) => [...list.slice(-3), { tone: 'success', ...t, id }]);
      window.setTimeout(() => dismiss(id), t.tone === 'error' ? 8000 : 5000);
    },
    [dismiss],
  );
  const value = useMemo(() => ({ toast }), [toast]);

  const render = (list: ToastItem[]) =>
    list.map((t) => (
      <div
        key={t.id}
        className="pointer-events-auto flex w-full items-start gap-3 rounded-card border border-border bg-card p-3 text-sm text-ink shadow-lg"
      >
        <span className="mt-0.5">{icons[t.tone ?? 'success']}</span>
        <div className="min-w-0 flex-1">
          <p className="font-medium">{t.title}</p>
          {t.description && <p className="mt-0.5 text-muted-ink">{t.description}</p>}
        </div>
        <button
          type="button"
          onClick={() => dismiss(t.id)}
          className="rounded p-0.5 text-muted-ink hover:text-ink"
          aria-label="Dismiss"
        >
          <X className="size-4" aria-hidden />
        </button>
      </div>
    ));

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 p-4 sm:items-end">
        <div role="status" aria-live="polite" className={cx('flex w-full max-w-sm flex-col gap-2')}>
          {render(items.filter((t) => t.tone !== 'error'))}
        </div>
        <div role="alert" aria-live="assertive" className="flex w-full max-w-sm flex-col gap-2">
          {render(items.filter((t) => t.tone === 'error'))}
        </div>
      </div>
    </ToastContext.Provider>
  );
}
